import React from "react";
import { useLocation, Link } from "react-router-dom";
import Seo from "../components/Seo";
import { eur } from "../lib/format";
import { orderPdfUrl } from "../lib/api";
import { CheckCircle2, Download, Landmark, Truck } from "lucide-react";

export default function OrderConfirmation() {
  const { state } = useLocation();
  let order = state?.order;
  if (!order) {
    try {
      order = JSON.parse(localStorage.getItem("fp_last_order") || "null");
    } catch {
      order = null;
    }
  }

  if (!order) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <h1 className="font-heading text-2xl">No hay pedido para mostrar</h1>
        <Link to="/tienda" className="text-brand mt-4 inline-block">Ir a la tienda</Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-10">
      <Seo title={`Pedido ${order.number} confirmado | FABRIPAN`} description="Su pedido ha sido confirmado." noindex />
      <div className="text-center">
        <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto" />
        <h1 className="font-heading font-bold text-3xl tracking-tighter mt-4">¡Pedido confirmado!</h1>
        <p className="text-muted-foreground mt-2">Gracias, {order.customer_name}. Hemos registrado su pedido.</p>
        <p className="font-mono text-lg mt-2" data-testid="order-number">Nº {order.number}</p>
      </div>

      <div className="mt-8 border border-brand/40 bg-brand/5 rounded-sm p-5" data-testid="transfer-instructions">
        <div className="flex items-center gap-2 font-heading font-semibold text-lg mb-3"><Landmark className="h-5 w-5 text-brand" /> Instrucciones de transferencia</div>
        <p className="text-sm text-muted-foreground mb-3">Realice una transferencia bancaria por el importe total indicando su número de pedido como concepto:</p>
        <div className="bg-white border border-border rounded-sm p-4 space-y-1 text-sm font-mono">
          <div className="flex justify-between"><span className="text-muted-foreground">IBAN</span><span>{order.bank_iban || "ES00 0000 0000 0000 0000 0000"}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Titular</span><span>{order.bank_holder || "FABRIPAN"}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Concepto</span><span>{order.number}</span></div>
          <div className="flex justify-between font-semibold text-brand"><span>Importe</span><span>{eur(order.total)}</span></div>
        </div>
      </div>

      <div className="mt-6 border border-border rounded-sm bg-white p-5">
        <h2 className="font-heading font-semibold mb-3">Resumen del pedido</h2>
        <div className="space-y-2">
          {order.items.map((i, idx) => (
            <div key={idx} className="flex justify-between text-sm">
              <span className="text-muted-foreground">{i.quantity} × {i.name}</span>
              <span className="font-mono">{eur(i.price * i.quantity)}</span>
            </div>
          ))}
        </div>
        <div className="border-t border-border mt-3 pt-3 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Base</span><span className="font-mono">{eur(order.subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">IVA</span><span className="font-mono">{eur(order.iva)}</span></div>
          <div className="flex justify-between font-heading font-bold"><span>Total</span><span className="text-brand">{eur(order.total)}</span></div>
        </div>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <a href={orderPdfUrl(order.id)} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark text-white font-semibold py-3 rounded-sm transition-colors" data-testid="download-order-pdf">
          <Download className="h-4 w-4" /> Descargar PDF del pedido
        </a>
        <Link to="/tienda" className="flex-1 inline-flex items-center justify-center border border-border hover:border-brand py-3 rounded-sm font-semibold transition-colors">
          Seguir comprando
        </Link>
      </div>
      <p className="text-xs text-muted-foreground mt-4 text-center flex items-center justify-center gap-1.5"><Truck className="h-3.5 w-3.5" /> Enviamos su pedido personalizado a su empresa.</p>
    </div>
  );
}
